import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, CircularProgress, Stack } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import React from 'react'
import { useLatestState } from 'src/Libs/flow/advanced-settings/hooks'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { syncInstallation } from 'src/Libs/flow/installation/api'
import { ModbusTcpChargingStation } from 'src/Libs/flow/installation/forms'
import { installationDevicesKey } from 'src/Libs/flow/installation/hooks'
import { getModbusTcpPUTDto } from 'src/Libs/flow/parsers'
import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch'
import { AccordionItem } from '@e3dc-react/shell/Elements/Accordion'
import { TabButton, TabButtonGroupBase } from 'src/Components/TabButton'
import { SettingInfoBox, SettingsBox } from 'src/Elements/Data'
import { ControlledModbusTcpPUTDto, updateEvse } from '../api/evse.api.js'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'
import useAppRatingContext from 'src/Libs/app-rating/hooks/use-app-rating-context.js'
import { usePortalMutation } from 'src/Libs/api'

const LOCK_CABLE_OPTIONS = [
  {
    value: false,
    labelKey: 'user-settings.evse-cable-page.options.unlocked',
    descriptionKey: 'user-settings.evse-cable-page.options.unlocked-description',
  },
  {
    value: true,
    labelKey: 'user-settings.evse-cable-page.options.locked',
    descriptionKey: 'user-settings.evse-cable-page.options.locked-description',
  },
]

interface LockCableProps {
  installationId: number
  queryIsLoading: boolean
  evse: ModbusTcpChargingStation
}

/** The lock cable page of an installation's (user-level) charging station settings. */
export const LockCable: React.FC<LockCableProps> = ({ installationId, queryIsLoading, evse }) => {
  const { t } = useCustomTranslation()
  const { triggerAppRating } = useAppRatingContext()
  const { enqueueErrorStack } = useSnackbar()
  const { hasInstallationWritingRights: userCanEdit } = useInstallationAccessRightsContext()

  const currentLockCable = evse.isCableLocked
  const [lockCable, setLockCable] = useLatestState(currentLockCable)

  const authFetch = useAuthFetchWithResult()
  const queryClient = useQueryClient()
  const mutateEvse = usePortalMutation({
    mutationFn: (newEvse: ControlledModbusTcpPUTDto) =>
      updateEvse(installationId, evse.rawData?.deviceLinkId?.toString() ?? '', evse.rawData?.id?.toString() ?? '', newEvse, authFetch).then(() =>
        syncInstallation(installationId, authFetch)
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: installationDevicesKey(installationId),
      }),
    onError: () => {
      enqueueErrorStack(t('common.update-failed-error'))
      setLockCable(currentLockCable)
    },
  })

  const updateLockCable = (newLockCable: boolean): void => {
    if (lockCable === newLockCable) return

    setLockCable(newLockCable)
    mutateEvse.mutate(getModbusTcpPUTDto({ ...evse, isCableLocked: newLockCable }))
  }

  const isLoading = queryIsLoading || mutateEvse.isPending
  const isDisabled = isLoading || !userCanEdit
  const selectedConfig = LOCK_CABLE_OPTIONS.find(opt => opt.value === lockCable) ?? LOCK_CABLE_OPTIONS[0]
  const defaultTabIndex = LOCK_CABLE_OPTIONS.findIndex(opt => opt.value === currentLockCable)

  return (
    <AccordionItem title={t('user-settings.evse-page.cable-lock')} expanded accordionDetailsSx={{ px: 4, py: 2 }}>
      <Stack>
        <SettingsBox
          title={t('user-settings.evse-cable-page.options.title')}
          suffix={
            <Box sx={{ minWidth: { xs: '100%', sm: '50%' } }}>
              <TabButtonGroupBase
                sx={{ borderRadius: '100px' }}
                key={`lock-cable-${currentLockCable}`}
                defaultTabIndex={defaultTabIndex > -1 ? defaultTabIndex : 0}
                disabled={isDisabled}
              >
                <TabButton
                  onClick={() => {
                    updateLockCable(false)
                    triggerAppRating()
                  }}
                >
                  {t(LOCK_CABLE_OPTIONS[0].labelKey)}
                </TabButton>
                <TabButton
                  onClick={() => {
                    updateLockCable(true)
                    triggerAppRating()
                  }}
                >
                  {t(LOCK_CABLE_OPTIONS[1].labelKey)}
                </TabButton>
              </TabButtonGroupBase>
            </Box>
          }
        />
        <SettingInfoBox title={t(selectedConfig.labelKey)} description={t(selectedConfig.descriptionKey)} />
      </Stack>
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress />
        </Box>
      )}
    </AccordionItem>
  )
}
